import { useEffect, useState } from 'react';
import BottomSheet from './BottomSheet';
import NumPad from './NumPad';

const EnquiryForm = ({ open, onClose, courses = [], onSubmit, saving, error }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [interestCourseId, setInterestCourseId] = useState('');
  const [notes, setNotes] = useState('');
  const [showPhonePad, setShowPhonePad] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName('');
    setLastName('');
    setPhone('');
    setInterestCourseId('');
    setNotes('');
    setShowPhonePad(false);
  }, [open]);

  const canSubmit = !saving && firstName.trim() && phone.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const interestName =
      courses.find((course) => course.id === interestCourseId)?.name || '';
    onSubmit?.({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      interestCourseId,
      interestCourseName: interestName,
      notes: notes.trim(),
    });
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Walk-in enquiry"
      description="Capture prospective students so admin can follow up later."
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save enquiry'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              First name
            </span>
            <input
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Last name (optional)
            </span>
            <input
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>

        <div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Phone
            </span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(event) =>
                setPhone(event.target.value.replace(/[^0-9+]/g, '').slice(0, 15))
              }
              onFocus={() => setShowPhonePad(true)}
              placeholder="07XXXXXXXX"
              className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 font-mono text-base text-stone-950 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>
          {showPhonePad ? (
            <div className="mt-3 rounded-2xl border border-stone-200 bg-stone-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                  Number pad
                </span>
                <button
                  type="button"
                  onClick={() => setShowPhonePad(false)}
                  className="text-xs font-semibold text-stone-600 hover:text-stone-900"
                >
                  Hide
                </button>
              </div>
              <NumPad
                value={phone}
                onChange={setPhone}
                extraKey="+"
                maxLength={15}
              />
            </div>
          ) : null}
        </div>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Interested in
          </span>
          <select
            value={interestCourseId}
            onChange={(event) => setInterestCourseId(event.target.value)}
            className="block min-h-[52px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="">Not sure yet</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name || course.title || course.id}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            placeholder="Preferred time, heard from, budget..."
            className="block w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>

        {error ? (
          <p className="rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}
      </div>
    </BottomSheet>
  );
};

export default EnquiryForm;
